/**
 * GoogleTagManager — CMS-driven Google analytics injection.
 *
 * Reads two independent fields from the CMS landing document:
 *   - `analytics.gtm_id`            → GTM container ID (format `GTM-XXXXXXX`)
 *   - `analytics.ga4_measurement_id`→ GA4 measurement ID (format `G-XXXXXXXXXX`)
 *
 * Behaviour:
 *   * Either, both, or neither can be set. Each is validated + injected independently.
 *   * GTM: head-script + noscript-iframe (standard install snippet).
 *   * GA4: async gtag.js loader + init call (standard direct install snippet).
 *   * Idempotent — never mounts the same tag twice, even across route changes.
 *   * Silent fallback — missing / malformed ID → no-op, no console noise.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";

const GTM_ID_RE = /^GTM-[A-Z0-9]+$/;
const GA4_ID_RE = /^G-[A-Z0-9]+$/;

function injectGTM(gtmId) {
  if (document.getElementById("gtm-head-script")) return;

  // Script bodies are executed as JS regardless of insertion method, so `textContent`
  // is preferred over `innerHTML` — the latter parses the string as HTML first,
  // which is an unnecessary attack surface even when the interpolated value is
  // regex-validated (defence in depth per code-review guidance).
  const s = document.createElement("script");
  s.id = "gtm-head-script";
  s.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');`;
  document.head.appendChild(s);

  // Build the noscript fallback by creating the iframe element directly instead
  // of assigning innerHTML — no HTML parsing of the interpolated gtmId occurs.
  const ns = document.createElement("noscript");
  ns.id = "gtm-body-noscript";
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  ns.appendChild(iframe);
  document.body.insertBefore(ns, document.body.firstChild);
}

function injectGA4(measurementId) {
  if (document.getElementById("ga4-loader-script")) return;

  // 1) Async loader — this is the same URL Google's install wizard emits.
  const loader = document.createElement("script");
  loader.id = "ga4-loader-script";
  loader.async = true;
  loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(loader);

  // 2) Init snippet — creates the dataLayer + gtag() shim and fires the initial
  //    config event. Uses textContent for the same defence-in-depth reason as GTM.
  const init = document.createElement("script");
  init.id = "ga4-init-script";
  init.textContent =
    `window.dataLayer = window.dataLayer || [];` +
    `function gtag(){dataLayer.push(arguments);}` +
    `gtag('js', new Date());` +
    `gtag('config', '${measurementId}');`;
  document.head.appendChild(init);
}

export default function GoogleTagManager() {
  const { data } = useQuery({
    queryKey: ["cms-landing"],  // shared cache — no extra HTTP call
    queryFn: () => api.get("/cms/landing").then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });

  useEffect(() => {
    const analytics = data?.content?.analytics || {};
    const gtmId = analytics.gtm_id?.trim();
    const ga4Id = analytics.ga4_measurement_id?.trim();
    if (gtmId && GTM_ID_RE.test(gtmId)) injectGTM(gtmId);
    if (ga4Id && GA4_ID_RE.test(ga4Id)) injectGA4(ga4Id);
  }, [data]);

  return null;
}
